import { useState, useEffect, useCallback } from 'react';
import { createDragState, startDrag, updateDrag, endDrag, type DragState } from '@/utils/drag';

interface UseDragToCloseOptions {
  onClose: () => void;
  threshold?: number;
}

export const useDragToClose = ({ onClose, threshold = 100 }: UseDragToCloseOptions) => {
  const [dragState, setDragState] = useState<DragState>(createDragState());

  const handleStart = useCallback((clientY: number) => {
    setDragState(startDrag(clientY));
  }, []);

  const handleMove = useCallback((clientY: number) => {
    setDragState((prev) => updateDrag(prev, clientY));
  }, []);

  const handleEnd = useCallback(() => {
    const { shouldClose, newState } = endDrag(dragState, threshold);
    setDragState(newState);

    if (shouldClose) {
      onClose();
    }
  }, [dragState, threshold, onClose]);

  useEffect(() => {
    if (!dragState.isDragging) return;

    const handleMouseMove = (e: MouseEvent) => handleMove(e.clientY);
    const handleMouseUp = () => handleEnd();
    const handleTouchMove = (e: TouchEvent) => handleMove(e.touches[0].clientY);
    const handleTouchEnd = () => handleEnd();

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [dragState.isDragging, handleMove, handleEnd]);

  return {
    dragState,
    handleMouseDown: (e: React.MouseEvent) => handleStart(e.clientY),
    handleTouchStart: (e: React.TouchEvent) => handleStart(e.touches[0].clientY),
  };
};
